import { useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import useAgentManagers from '../hooks/useAgentManagers';
import AgentManagerDialog from './managersAgents/AgentManagerDialog';
import ManagerCard from './managersAgents/ManagerCard';
import { agentLabel } from './managersAgents/agentManagerUtils';

const TabHeader = ({ canEdit, onAdd }) => (
  <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1}>
    <Box display="flex" alignItems="center" gap={1}>
      <SupervisorAccountIcon sx={{ color: '#0054A5' }} />
      <Typography variant="h6" color="#0054A5">
        מנהלים וסוכנים
      </Typography>
    </Box>
    {canEdit && (
      <Button
        variant="contained"
        startIcon={<PersonAddIcon />}
        onClick={onAdd}
        sx={{ bgcolor: '#0054A5', '&:hover': { bgcolor: '#003d7a' } }}
      >
        הוסף סוכן
      </Button>
    )}
  </Box>
);

const ManagersList = ({ agentManagers, onEdit, onRemove }) =>
  agentManagers.managers.length === 0 ? (
    <Alert severity="warning">לא הוגדרו מנהלים</Alert>
  ) : (
    agentManagers.managers.map((manager) => (
      <ManagerCard
        key={manager.email}
        manager={manager}
        agentEmails={agentManagers.agentEmails}
        branchNames={agentManagers.branchNames}
        canEdit={agentManagers.canEdit}
        onEdit={onEdit}
        onRemove={onRemove}
      />
    ))
  );

// Each agent gets a manager (a portal user) and optionally specific branches
// (empty = all). The manager gets the trade-delay emails for those orders and
// sees only them. Admin edits; everyone else views.
const ManagersAgentsTab = ({ execute }) => {
  const agentManagers = useAgentManagers(execute);
  const [actionError, setActionError] = useState('');
  // null = closed, {} = add, row = edit
  const [dialogRow, setDialogRow] = useState(null);

  const handleSave = async (payload) => {
    await agentManagers.save(payload, dialogRow?._id);
    setDialogRow(null);
  };

  const handleRemove = async (row) => {
    if (!window.confirm(`להסיר את ${agentLabel(row)} מ${row.managerName}?`)) return;
    setActionError('');
    try {
      await agentManagers.remove(row._id);
    } catch {
      setActionError('שגיאה בהסרת הסוכן');
    }
  };

  if (agentManagers.loading) return <CircularProgress size={24} sx={{ m: 2 }} />;
  if (agentManagers.loadError) return <Alert severity="error">{agentManagers.loadError}</Alert>;

  return (
    <Box>
      <TabHeader canEdit={agentManagers.canEdit} onAdd={() => setDialogRow({})} />
      {agentManagers.agentsError && <Alert severity="error" sx={{ mb: 2 }}>{agentManagers.agentsError}</Alert>}
      {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}
      <ManagersList agentManagers={agentManagers} onEdit={setDialogRow} onRemove={handleRemove} />
      {dialogRow && (
        <AgentManagerDialog
          initial={dialogRow._id ? dialogRow : null}
          rows={agentManagers.rows}
          users={agentManagers.users}
          branchOptions={agentManagers.branchOptions}
          agentOptions={agentManagers.agentOptions}
          agentsLoading={agentManagers.agentsLoading}
          onSave={handleSave}
          onCancel={() => setDialogRow(null)}
        />
      )}
    </Box>
  );
};

export default ManagersAgentsTab;
